import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, TrendingDown, Minus, X, Zap, AlertTriangle, Loader2 } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import { useToast } from '../components/Toast';

// ─── API endpoints ─────────────────────────────────────────────────────────────
const DYDX_API  = 'https://indexer.dydx.trade/v4';
const APEX_BASE = 'https://pro.apex.exchange/api';

// ─── Fees ─────────────────────────────────────────────────────────────────────
const DYDX_MAKER  = 0.0002;
const DYDX_TAKER  = 0.0005;
const APEX_MAKER  = 0.0002;  // 0.02%
const APEX_TAKER  = 0.0005;  // 0.05%

// ─── Symbol mapping: Apex symbol → dYdX symbol ──────────────────────────────
// Apex uses "BTCUSDC" format (no dash), dYdX uses "BTC-USD"
const APEX_TO_DYDX: Record<string, string> = {
  'BTCUSDC':    'BTC-USD',
  'ETHUSDC':    'ETH-USD',
  'SOLUSDC':    'SOL-USD',
  'SUIUSDC':    'SUI-USD',
  'HYPEUSDC':   'HYPE-USD',
  'AVAXUSDC':   'AVAX-USD',
  'BNBUSDC':    'BNB-USD',
  'XRPUSDC':    'XRP-USD',
  'DOGEUSDC':   'DOGE-USD',
  'ADAUSDC':    'ADA-USD',
  'LINKUSDC':   'LINK-USD',
  'OPUSDC':     'OP-USD',
  'UNIUSDC':    'UNI-USD',
  'AAVEUSDC':   'AAVE-USD',
};

// Apex symbol to base asset name
const apexBase = (sym: string) => sym.replace('USDC', '').replace('USDT', '');

// ─── Types ─────────────────────────────────────────────────────────────────────
interface ApexTicker {
  symbol:          string;
  lastPrice:       string;
  bidPrice:        string;
  askPrice:        string;
  fundingRate:     string;
  nextFundingRate: string;
  nextFundingAt:   string;
  indexPrice:      string;
  markPrice:       string;
  priceChangeRate: string;
  volume24h:       string;
}

interface DydxMarket {
  ticker:            string;
  nextFundingRate:   string;
  oraclePrice:       string;
  initialMarginFraction: string;
  status:            string;
}

interface DydxOrderbook {
  bids: { price: string; size: string }[];
  asks: { price: string; size: string }[];
}

interface Row {
  base:            string;
  apexSymbol:      string;
  dydxSymbol:      string;
  apexBid:         number;
  apexAsk:         number;
  apexMidSpreadPct: number;
  apexFunding:     number;
  apexOracle:      number;
  apexMaxLev:      number;
  dydxBid:         number;
  dydxAsk:         number;
  dydxMidSpreadPct: number;
  dydxFunding:     number;
  dydxOracle:      number;
  dydxMaxLev:      number;
  netShortDydxLongApex: number;
  netLongDydxShortApex: number;
  bestRate:        number;
  bestDir:         'S_DYDX_L_APEX' | 'L_DYDX_S_APEX' | 'NONE';
}

// ─── Arb history ──────────────────────────────────────────────────────────────
const HISTORY_KEY = 'dydxapex_arb_v1';

interface ArbRecord {
  id:             string;
  base:           string;
  dydxSymbol:     string;
  apexSymbol:     string;
  openTime:       number;
  dydxDir:        'long' | 'short';
  apexDir:        string;
  qty:            number;
  dydxOpenPrice:  number;
  apexOpenPrice:  number;
  postOnly:       boolean;
  closeTime?:     number;
  dydxClosePrice?: number;
  apexClosePrice?: number;
  status:         'open' | 'closed';
}

function loadHistory(): ArbRecord[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
  catch { return []; }
}
function saveHistory(records: ArbRecord[]) {
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(records)); } catch {}
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
const pct = (n: number, d = 4) => `${(n * 100).toFixed(d)}%`;
const fmt = (n: number) =>
  n >= 1000 ? n.toLocaleString('en-US', { maximumFractionDigits: 2 })
  : n >= 1  ? n.toFixed(4)
  : n.toFixed(6);

// ─── Sub-components ────────────────────────────────────────────────────────────
function FeeCard({ label, maker, taker, dark, color = 'purple' }: {
  label: string; maker: number; taker: number; dark: boolean; color?: string;
}) {
  const t = getTheme(dark);
  const accent = color === 'orange' ? 'text-orange-400' : 'text-blue-500';
  return (
    <div className={`rounded-xl border ${t.cardBorder} ${t.card} p-4`}>
      <p className={`text-[11px] font-bold uppercase tracking-widest ${accent} mb-2`}>{label}</p>
      <div className="flex gap-4">
        <div>
          <p className={`text-[10px] ${accent}`}>Maker</p>
          <p className="text-sm font-bold text-emerald-400">{pct(maker, 3)}</p>
        </div>
        <div>
          <p className={`text-[10px] ${accent}`}>Taker</p>
          <p className="text-sm font-bold text-rose-400">{pct(taker, 3)}</p>
        </div>
      </div>
    </div>
  );
}

function FundingBadge({ rate }: { rate: number }) {
  const color = rate > 0.00001 ? 'text-emerald-400' : rate < -0.00001 ? 'text-rose-400' : 'text-blue-400';
  const icon  = rate > 0.00001 ? <TrendingUp size={10} /> : rate < -0.00001 ? <TrendingDown size={10} /> : <Minus size={10} />;
  return (
    <span className={`inline-flex items-center gap-0.5 font-mono text-xs ${color}`}>
      {icon}{pct(rate, 5)}
    </span>
  );
}

// ─── Order Modal ───────────────────────────────────────────────────────────────
type SizeUnit = 'USDC' | 'TOKEN';

interface ModalState {
  dydxDir:    'LONG' | 'SHORT';
  apexDir:    'LONG' | 'SHORT';
  dydxPrice:  string;
  apexPrice:  string;
  sizeRaw:    string;
  sizeUnit:   SizeUnit;
  dydxLev:    number;
  apexLev:    number;
}

const LEVERAGES = [1, 2, 3, 5, 10, 20];

function DirToggle({
  value, onChange, exchange,
}: { value: 'LONG' | 'SHORT'; onChange: (d: 'LONG' | 'SHORT') => void; exchange: 'dydx' | 'apex' }) {
  const accent = exchange === 'dydx' ? 'border-blue-500/60' : 'border-orange-500/60';
  return (
    <div className={`flex rounded-lg border overflow-hidden text-xs font-black ${accent}`}>
      <button
        onClick={() => onChange('LONG')}
        className={`flex-1 py-1.5 transition-colors ${
          value === 'LONG' ? 'bg-emerald-500/30 text-emerald-300' : 'bg-[#061530] text-blue-600 hover:text-emerald-500'
        }`}
      >LONG</button>
      <button
        onClick={() => onChange('SHORT')}
        className={`flex-1 py-1.5 border-l border-[#0a2560] transition-colors ${
          value === 'SHORT' ? 'bg-rose-500/30 text-rose-300' : 'bg-[#061530] text-blue-600 hover:text-rose-500'
        }`}
      >SHORT</button>
    </div>
  );
}

function LevPicker({ value, onChange }: { value: number; onChange: (l: number) => void }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {LEVERAGES.map(lev => (
        <button
          key={lev}
          onClick={() => onChange(lev)}
          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-colors ${
            value === lev
              ? 'bg-[#4A9FE8]/20 border-[#4A9FE8] text-[#4A9FE8]'
              : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-blue-500 hover:text-blue-300'
          }`}
        >{lev}×</button>
      ))}
    </div>
  );
}

function OrderModal({ row, dark, onClose, sendWs, onRecordOpen }: {
  row: Row; dark: boolean; onClose: () => void;
  sendWs: (msg: object) => void;
  onRecordOpen?: (r: ArbRecord) => void;
}) {
  const t = getTheme(dark);
  const { showToast } = useToast();
  const [sending,  setSending]  = useState(false);
  const [postOnly, setPostOnly] = useState(false);

  const defaultDydxDir: 'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_APEX' ? 'SHORT' : 'LONG';
  const defaultApexDir: 'LONG' | 'SHORT' = row.bestDir === 'S_DYDX_L_APEX' ? 'LONG'  : 'SHORT';

  const priceStr = (p: number) => p.toFixed(p >= 100 ? 2 : 4);

  const [modal, setModal] = useState<ModalState>({
    dydxDir:  defaultDydxDir,
    apexDir:  defaultApexDir,
    dydxPrice: priceStr(defaultDydxDir === 'LONG' ? row.dydxAsk : row.dydxBid),
    apexPrice: priceStr(defaultApexDir === 'LONG' ? row.apexAsk : row.apexBid),
    sizeRaw:  '1000',
    sizeUnit: 'USDC',
    dydxLev:  5,
    apexLev:  10,
  });

  const setDydxDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, dydxDir: d,
    dydxPrice: priceStr(d === 'LONG' ? row.dydxAsk : row.dydxBid),
  }));
  const setApexDir = (d: 'LONG' | 'SHORT') => setModal(m => ({
    ...m, apexDir: d,
    apexPrice: priceStr(d === 'LONG' ? row.apexAsk : row.apexBid),
  }));

  const dirBg = (d: string) => d === 'LONG'
    ? 'bg-emerald-500/10 border-emerald-500/30'
    : 'bg-rose-500/10 border-rose-500/30';

  const midPrice = row.dydxOracle || (row.dydxBid + row.dydxAsk) / 2 || 1;
  const rawVal   = parseFloat(modal.sizeRaw) || 0;
  const sizeUsd  = modal.sizeUnit === 'USDC' ? rawVal : rawVal * midPrice;
  const tokenAmt = modal.sizeUnit === 'TOKEN' ? rawVal : rawVal / midPrice;
  const tokenDec = midPrice > 1000 ? 6 : midPrice > 1 ? 4 : 2;

  const toggleUnit = () => setModal(m => {
    const cur = parseFloat(m.sizeRaw) || 0;
    return m.sizeUnit === 'USDC'
      ? { ...m, sizeUnit: 'TOKEN', sizeRaw: (midPrice > 0 ? cur / midPrice : 0).toFixed(tokenDec) }
      : { ...m, sizeUnit: 'USDC',  sizeRaw: (cur * midPrice).toFixed(2) };
  });

  const entryFeePct = DYDX_MAKER + APEX_MAKER;
  const roundTrip   = entryFeePct * 2;
  const earnPerHour = row.bestRate * sizeUsd;
  const breakEvenH  = row.bestRate > 0 ? roundTrip / row.bestRate : Infinity;
  const breakEvenMin = isFinite(breakEvenH) ? breakEvenH * 60 : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className={`w-full max-w-2xl rounded-2xl border ${t.cardBorder} bg-[#030d1f] shadow-2xl`}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0a2560]">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-[#4A9FE8]">{row.base}</span>
            <span className="text-[11px] text-blue-500">funding arb</span>
            {row.bestRate > 0 && (
              <span className="text-[11px] font-mono text-emerald-400 bg-emerald-900/30 px-2 py-0.5 rounded-full border border-emerald-700/40">
                +{pct(row.bestRate, 4)}/h
              </span>
            )}
            <span className="text-[11px] text-blue-600 ml-1">{modal.dydxDir} dYdX · {modal.apexDir} Apex</span>
          </div>
          <button onClick={onClose} className="text-blue-500 hover:text-white transition-colors p-1">
            <X size={16} />
          </button>
        </div>

        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">

            {/* dYdX column */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.dydxDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">dYdX</span>
                <span className="text-[10px] text-blue-600">Post-Only · Maker {pct(DYDX_MAKER, 3)}</span>
              </div>
              <DirToggle value={modal.dydxDir} onChange={setDydxDir} exchange="dydx" />
              <div>
                <input
                  type="number"
                  value={modal.dydxPrice}
                  onChange={e => setModal(m => ({ ...m, dydxPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.dydxPrice === priceStr(row.dydxBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-emerald-600 hover:text-emerald-400'}`}
                  >Bid {fmt(row.dydxBid)}</button>
                  <button onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr((row.dydxBid + row.dydxAsk) / 2) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.dydxPrice === priceStr((row.dydxBid + row.dydxAsk) / 2) ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'}`}
                  >Mid {fmt((row.dydxBid + row.dydxAsk) / 2)}</button>
                  <button onClick={() => setModal(m => ({ ...m, dydxPrice: priceStr(row.dydxAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.dydxPrice === priceStr(row.dydxAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-blue-500 hover:border-rose-600 hover:text-rose-400'}`}
                  >Ask {fmt(row.dydxAsk)}</button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.dydxMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.dydxMaxLev}×</span>}
                </div>
                <LevPicker value={modal.dydxLev} onChange={lev => setModal(m => ({ ...m, dydxLev: lev }))} />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h</span>
                <FundingBadge rate={row.dydxFunding} />
              </div>
            </div>

            {/* Apex column */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(modal.apexDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-orange-400 uppercase tracking-wider">Apex Pro</span>
                <span className="text-[10px] text-blue-600">Post-Only · Maker {pct(APEX_MAKER, 3)}</span>
              </div>
              <DirToggle value={modal.apexDir} onChange={setApexDir} exchange="apex" />
              <div>
                <input
                  type="number"
                  value={modal.apexPrice}
                  onChange={e => setModal(m => ({ ...m, apexPrice: e.target.value }))}
                  className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder="Limit fiyat"
                />
                <div className="flex gap-1 mt-1">
                  <button onClick={() => setModal(m => ({ ...m, apexPrice: priceStr(row.apexBid) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.apexPrice === priceStr(row.apexBid) ? 'bg-emerald-500/20 border-emerald-500/60 text-emerald-300' : 'bg-[#061530] border-[#0a2560] text-orange-800 hover:border-emerald-600 hover:text-emerald-400'}`}
                  >Bid {fmt(row.apexBid)}</button>
                  <button onClick={() => setModal(m => ({ ...m, apexPrice: priceStr((row.apexBid + row.apexAsk) / 2) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.apexPrice === priceStr((row.apexBid + row.apexAsk) / 2) ? 'bg-[#4A9FE8]/20 border-[#4A9FE8]/60 text-[#4A9FE8]' : 'bg-[#061530] border-[#0a2560] text-orange-800 hover:border-[#4A9FE8]/60 hover:text-[#4A9FE8]'}`}
                  >Mid {fmt((row.apexBid + row.apexAsk) / 2)}</button>
                  <button onClick={() => setModal(m => ({ ...m, apexPrice: priceStr(row.apexAsk) }))}
                    className={`flex-1 py-0.5 rounded border text-[10px] font-bold font-mono transition-colors ${modal.apexPrice === priceStr(row.apexAsk) ? 'bg-rose-500/20 border-rose-500/60 text-rose-300' : 'bg-[#061530] border-[#0a2560] text-orange-800 hover:border-rose-600 hover:text-rose-400'}`}
                  >Ask {fmt(row.apexAsk)}</button>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-[10px] text-blue-500">Kaldıraç</p>
                  {row.apexMaxLev > 0 && <span className="text-[9px] text-blue-600">Max {row.apexMaxLev}×</span>}
                </div>
                <LevPicker value={modal.apexLev} onChange={lev => setModal(m => ({ ...m, apexLev: lev }))} />
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-blue-500">Funding 1h (next)</span>
                <FundingBadge rate={row.apexFunding} />
              </div>
            </div>
          </div>

          {/* Size */}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={modal.sizeRaw}
                  onChange={e => setModal(m => ({ ...m, sizeRaw: e.target.value }))}
                  className="flex-1 w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                  placeholder={modal.sizeUnit === 'USDC' ? '1000' : '0.01'}
                />
                <button
                  onClick={toggleUnit}
                  className="flex-shrink-0 flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold"
                >
                  <span className={`px-2.5 py-1.5 transition-colors ${modal.sizeUnit === 'USDC' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>USDC</span>
                  <span className={`px-2.5 py-1.5 border-l border-[#0a2560] transition-colors ${modal.sizeUnit === 'TOKEN' ? 'bg-[#4A9FE8]/20 text-[#4A9FE8]' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}>{row.base}</span>
                </button>
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-blue-600">
                {modal.sizeUnit === 'USDC'
                  ? <span>≈ <span className="font-mono text-blue-400">{tokenAmt.toFixed(tokenDec)} {row.base}</span></span>
                  : <span>≈ <span className="font-mono text-blue-400">${sizeUsd.toLocaleString('en-US', { maximumFractionDigits: 0 })} USDC</span></span>
                }
                <span>Toplam teminat: <span className="font-mono text-blue-400">${(sizeUsd * 2).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span></span>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-[#061530]/80 rounded-xl border border-[#0a2560] px-3 py-2">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1 text-[11px]">
              <div className="flex justify-between">
                <span className="text-blue-500">Round-trip fee</span>
                <span className="font-mono text-rose-400">−{pct(roundTrip, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Net / saat</span>
                <span className="font-mono text-emerald-400">+{pct(row.bestRate, 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Break-even</span>
                <span className="font-mono text-[#4A9FE8]">{breakEvenMin !== null ? `~${breakEvenMin.toFixed(0)} dk` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Saatlik gelir</span>
                <span className="font-mono text-emerald-400">{sizeUsd > 0 ? `$${earnPerHour.toFixed(3)}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">dYdX notional ×{modal.dydxLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.dydxLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-500">Apex notional ×{modal.apexLev}</span>
                <span className="font-mono text-white">{sizeUsd > 0 ? `$${(sizeUsd * modal.apexLev).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</span>
              </div>
            </div>
          </div>

          {/* Maker/Taker */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-500">Emir tipi</span>
            <div className="flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
              <button
                onClick={() => setPostOnly(true)}
                className={`px-3 py-1.5 transition-colors ${postOnly ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Maker (Post-Only)</button>
              <button
                onClick={() => setPostOnly(false)}
                className={`px-3 py-1.5 border-l border-[#0a2560] transition-colors ${!postOnly ? 'bg-rose-500/20 text-rose-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Taker (Limit)</button>
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-[10px] text-yellow-600">
            <AlertTriangle size={11} className="flex-shrink-0" />
            <span>{postOnly ? 'Post-only emir fiyat değişiminde reddedilebilir.' : 'Taker emir piyasada dolar, yüksek fee uygulanır.'} Apex Pro StarkEx imzası gerektirir — APEX_STARK_KEY env\'i ayarlandığından emin ol.</span>
          </div>

          {/* Buttons */}
          <div className="flex gap-2.5">
            <button
              onClick={onClose}
              disabled={sending}
              className="flex-1 py-2 rounded-xl border border-[#0a2560] text-blue-400 text-xs font-semibold hover:bg-[#0a2560]/40 transition-colors disabled:opacity-40"
            >İptal</button>
            <button
              disabled={sending || !tokenAmt || tokenAmt <= 0}
              onClick={async () => {
                if (sending) return;
                setSending(true);
                try {
                  sendWs({
                    type: 'manual_arb_order_apex',
                    payload: {
                      post_only: postOnly,
                      dydx: {
                        symbol:   row.dydxSymbol,
                        side:     modal.dydxDir.toLowerCase(),
                        price:    parseFloat(modal.dydxPrice),
                        quantity: parseFloat(tokenAmt.toFixed(8)),
                        leverage: modal.dydxLev,
                      },
                      apex: {
                        symbol:   row.apexSymbol,
                        side:     modal.apexDir.toLowerCase(),
                        price:    parseFloat(modal.apexPrice),
                        quantity: parseFloat(tokenAmt.toFixed(8)),
                        leverage: modal.apexLev,
                      },
                    },
                  });
                  await new Promise<void>((resolve) => {
                    const handler = (e: MessageEvent) => {
                      try {
                        const d = JSON.parse(e.data);
                        if (d.type === 'arb_order_result_apex') {
                          window.removeEventListener('ws_message', handler as any);
                          if (d.payload?.success) {
                            showToast('Apex Arb Emirleri Gönderildi', `dYdX ${modal.dydxDir} + Apex ${modal.apexDir} — ${row.base}`, 'success');
                            onRecordOpen?.({
                              id:           `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
                              base:         row.base,
                              dydxSymbol:   row.dydxSymbol,
                              apexSymbol:   row.apexSymbol,
                              openTime:     Date.now(),
                              dydxDir:      modal.dydxDir.toLowerCase() as 'long' | 'short',
                              apexDir:      modal.apexDir.toLowerCase(),
                              qty:          parseFloat(tokenAmt.toFixed(8)),
                              dydxOpenPrice: parseFloat(modal.dydxPrice),
                              apexOpenPrice: parseFloat(modal.apexPrice),
                              postOnly,
                              status: 'open',
                            });
                            onClose();
                          } else {
                            const dErr = d.payload?.dydx?.error;
                            const aErr = d.payload?.apex?.error;
                            showToast('Emir Hatası', [dErr, aErr].filter(Boolean).join(' | '), 'error');
                          }
                          resolve();
                        }
                      } catch {}
                    };
                    window.addEventListener('ws_message', handler as any);
                    setTimeout(() => { window.removeEventListener('ws_message', handler as any); resolve(); }, 20000);
                  });
                } finally {
                  setSending(false);
                }
              }}
              className="flex-1 py-2 rounded-xl bg-[#4A9FE8]/10 border border-[#4A9FE8]/30 text-[#4A9FE8] text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-[#4A9FE8]/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending
                ? <><Loader2 size={13} className="animate-spin" /> Gönderiliyor...</>
                : <><Zap size={13} /> Her İki Emri Gönder</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Arb Button ──────────────────────────────────────────────────────────────
function ArbButton({ row, onOpen }: { row: Row; onOpen: (r: Row) => void }) {
  if (row.bestDir === 'NONE') return <span className="text-blue-600 text-xs">—</span>;
  const hot   = row.bestRate > 0.0001;
  const label = row.bestDir === 'S_DYDX_L_APEX' ? 'S dYdX / L Apex' : 'L dYdX / S Apex';
  return (
    <button
      onClick={() => onOpen(row)}
      className={`flex flex-col items-end gap-0.5 group transition-all ${hot ? 'opacity-100' : 'opacity-75 hover:opacity-100'}`}
    >
      <span className={`text-[11px] font-mono font-bold px-2 py-1 rounded-lg border transition-all ${
        hot
          ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 group-hover:bg-emerald-500/35 group-hover:border-emerald-400'
          : 'bg-[#0a2560]/60 text-blue-400 border-[#0a2560] group-hover:border-blue-500 group-hover:text-blue-300'
      }`}>
        {pct(row.bestRate, 4)}/h ↗
      </span>
      <span className="text-[9px] text-blue-600 group-hover:text-blue-400 transition-colors">{label}</span>
    </button>
  );
}

// ─── Close Modal ─────────────────────────────────────────────────────────────
interface CloseCard {
  base:        string;
  dydxTkr:     string;
  apexSymbol:  string;
  dydxSide:    'LONG' | 'SHORT';
  apexSide:    string;
  dydxSize:    number;
  apexSize:    number;
  matchRow:    Row | null;
}

function CloseModal({ card, dark, onClose, sendWs, onRecordClose }: {
  card: CloseCard; dark: boolean; onClose: () => void;
  sendWs: (msg: object) => void;
  onRecordClose?: (base: string, dydxDir: string, closeTime: number, dydxClosePrice: number, apexClosePrice: number) => void;
}) {
  const t = getTheme(dark);
  const { showToast } = useToast();
  const [sending,  setSending]  = useState(false);
  const [postOnly, setPostOnly] = useState(true);

  const row = card.matchRow;
  const priceStr = (p: number) => p > 0 ? p.toFixed(p >= 100 ? 2 : 4) : '';

  const dydxCloseDir: 'LONG' | 'SHORT' = card.dydxSide === 'LONG' ? 'SHORT' : 'LONG';
  const apexCloseDir: 'LONG' | 'SHORT' = card.apexSide === 'long' ? 'SHORT' : 'LONG';

  const initDydxPrice = row ? priceStr(dydxCloseDir === 'SHORT' ? row.dydxAsk : row.dydxBid) : '';
  const initApexPrice = row ? priceStr(apexCloseDir === 'SHORT' ? row.apexAsk : row.apexBid) : '';

  const [dydxPrice, setDydxPrice] = useState(initDydxPrice);
  const [apexPrice, setApexPrice] = useState(initApexPrice);

  const dirBg = (d: 'LONG' | 'SHORT') =>
    d === 'LONG' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-rose-500/10 border-rose-500/30';
  const dirLabel = (d: 'LONG' | 'SHORT') => d === 'LONG'
    ? <span className="font-bold text-emerald-400">BUY / LONG</span>
    : <span className="font-bold text-rose-400">SELL / SHORT</span>;

  const canSend = parseFloat(dydxPrice) > 0 && parseFloat(apexPrice) > 0 && card.dydxSize > 0 && card.apexSize > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-3">
      <div className={`w-full max-w-xl rounded-2xl border ${t.cardBorder} bg-[#030d1f] shadow-2xl`}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#0a2560]">
          <div className="flex items-center gap-2">
            <span className="text-base font-black text-[#4A9FE8]">{card.base}</span>
            <span className="text-[11px] text-rose-400 bg-rose-900/30 px-2 py-0.5 rounded-full border border-rose-700/40">Pozisyon Kapat</span>
            <span className="text-[10px] text-blue-500">dYdX {card.dydxSide} · Apex {card.apexSide.toUpperCase()}</span>
          </div>
          <button onClick={onClose} className="text-blue-500 hover:text-white p-1"><X size={16} /></button>
        </div>

        <div className="p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5">
            {/* dYdX */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(dydxCloseDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-blue-300 uppercase tracking-wider">dYdX</span>
                <span className="text-[10px] text-blue-600">{pct(DYDX_MAKER, 3)} maker</span>
              </div>
              <div className="text-[11px]">{dirLabel(dydxCloseDir)}</div>
              <input type="number" value={dydxPrice} onChange={e => setDydxPrice(e.target.value)}
                className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                placeholder="Limit fiyat" />
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-blue-500">Miktar</span>
                <span className="font-mono text-white">{card.dydxSize.toFixed(6)} {card.base}</span>
              </div>
            </div>

            {/* Apex */}
            <div className={`rounded-xl border p-3 space-y-2 ${dirBg(apexCloseDir)}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-orange-400 uppercase tracking-wider">Apex Pro</span>
                <span className="text-[10px] text-blue-600">{pct(APEX_MAKER, 3)} maker</span>
              </div>
              <div className="text-[11px]">{dirLabel(apexCloseDir)}</div>
              <input type="number" value={apexPrice} onChange={e => setApexPrice(e.target.value)}
                className="w-full bg-[#061530] border border-[#0a2560] rounded-lg px-2.5 py-1.5 text-xs font-mono text-white focus:border-[#4A9FE8] outline-none"
                placeholder="Limit fiyat" />
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-blue-500">Miktar</span>
                <span className="font-mono text-white">{card.apexSize.toFixed(6)} {card.base}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-500">Emir tipi</span>
            <div className="flex rounded-lg border border-[#0a2560] overflow-hidden text-[10px] font-bold">
              <button onClick={() => setPostOnly(true)}
                className={`px-3 py-1.5 transition-colors ${postOnly ? 'bg-emerald-500/20 text-emerald-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Maker (Post-Only)</button>
              <button onClick={() => setPostOnly(false)}
                className={`px-3 py-1.5 border-l border-[#0a2560] transition-colors ${!postOnly ? 'bg-rose-500/20 text-rose-300' : 'bg-[#061530] text-blue-500 hover:text-blue-300'}`}
              >Taker (Limit)</button>
            </div>
          </div>

          <div className="flex gap-2.5">
            <button onClick={onClose} disabled={sending}
              className="flex-1 py-2 rounded-xl border border-[#0a2560] text-blue-400 text-xs font-semibold hover:bg-[#0a2560]/40 disabled:opacity-40"
            >İptal</button>
            <button
              disabled={sending || !canSend}
              onClick={async () => {
                if (sending) return;
                setSending(true);
                try {
                  sendWs({
                    type: 'manual_arb_order_apex',
                    payload: {
                      post_only: postOnly,
                      dydx: { symbol: card.dydxTkr, side: dydxCloseDir.toLowerCase(), price: parseFloat(dydxPrice), quantity: parseFloat(card.dydxSize.toFixed(8)), leverage: 1 },
                      apex: { symbol: card.apexSymbol, side: apexCloseDir.toLowerCase(), price: parseFloat(apexPrice), quantity: parseFloat(card.apexSize.toFixed(8)), leverage: 1 },
                    },
                  });
                  await new Promise<void>((resolve) => {
                    const handler = (e: MessageEvent) => {
                      try {
                        const d = JSON.parse((e as any).data ?? e);
                        if (d.type === 'arb_order_result_apex') {
                          window.removeEventListener('ws_message', handler as any);
                          if (d.payload?.success) {
                            showToast('Kapatma Emirleri Gönderildi', `dYdX + Apex ${card.base} kapatılıyor`, 'success');
                            onRecordClose?.(card.base, card.dydxSide.toLowerCase(), Date.now(), parseFloat(dydxPrice), parseFloat(apexPrice));
                            onClose();
                          } else {
                            const dErr = d.payload?.dydx?.error;
                            const aErr = d.payload?.apex?.error;
                            showToast('Emir Hatası', [dErr, aErr].filter(Boolean).join(' | '), 'error');
                          }
                          resolve();
                        }
                      } catch {}
                    };
                    window.addEventListener('ws_message', handler as any);
                    setTimeout(() => { window.removeEventListener('ws_message', handler as any); resolve(); }, 20000);
                  });
                } finally { setSending(false); }
              }}
              className="flex-1 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs font-semibold flex items-center justify-center gap-1.5 hover:bg-rose-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sending ? <><Loader2 size={13} className="animate-spin" /> Gönderiliyor...</> : <><X size={13} /> Her İki Pozisyonu Kapat</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
type Tab = 'dashboard' | 'gecmis';

export default function DydxApex({ dark }: { dark: boolean }) {
  const t = getTheme(dark);
  const { sendWs } = useWS() as any;
  const [tab,        setTab]        = useState<Tab>('dashboard');
  const [rows,       setRows]       = useState<Row[]>([]);
  const [loading,    setLoading]    = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error,      setError]      = useState<string | null>(null);
  const [modalRow,   setModalRow]   = useState<Row | null>(null);
  const [closeCard,  setCloseCard]  = useState<CloseCard | null>(null);
  const [arbHistory, setArbHistory] = useState<ArbRecord[]>(() => loadHistory());

  const handleRecordOpen = useCallback((r: ArbRecord) => {
    setArbHistory(prev => { const u = [...prev, r]; saveHistory(u); return u; });
  }, []);

  const handleRecordClose = useCallback((base: string, dydxDir: string, closeTime: number, dydxClosePrice: number, apexClosePrice: number) => {
    setArbHistory(prev => {
      let closed = false;
      const u = [...prev].reverse().map(r => {
        if (!closed && r.base === base && r.dydxDir === dydxDir && r.status === 'open') {
          closed = true;
          return { ...r, closeTime, dydxClosePrice, apexClosePrice, status: 'closed' as const };
        }
        return r;
      }).reverse();
      saveHistory(u);
      return u;
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setError(null);

      // ── Apex Pro tickers ──────────────────────────────────────────────────
      let apexTickerMap: Record<string, ApexTicker> = {};
      try {
        const apexRes  = await fetch(`${APEX_BASE}/v1/tickers`);
        if (apexRes.ok) {
          const apexJson = await apexRes.json();
          const tickers: ApexTicker[] = Array.isArray(apexJson.data) ? apexJson.data : [];
          tickers.forEach(t => { apexTickerMap[t.symbol] = t; });
        }
      } catch (e) {
        console.warn('Apex tickers fetch failed:', e);
      }

      // ── Apex symbols for market info ───────────────────────────────────────
      let apexMaxLevMap: Record<string, number> = {};
      try {
        const symRes = await fetch(`${APEX_BASE}/v1/symbols`);
        if (symRes.ok) {
          const symJson = await symRes.json();
          const contracts = symJson.data?.perpetualContract || [];
          contracts.forEach((c: any) => {
            apexMaxLevMap[c.symbol] = parseInt(c.maxLeverage || '20');
          });
        }
      } catch {}

      // ── dYdX markets ──────────────────────────────────────────────────────
      const dydxRes  = await fetch(`${DYDX_API}/perpetualMarkets`);
      const dydxData = await dydxRes.json();
      const dydxMap: Record<string, DydxMarket> = dydxData.markets || {};

      // ── Build rows ────────────────────────────────────────────────────────
      const built: Row[] = [];

      for (const [apexSym, dydxTkr] of Object.entries(APEX_TO_DYDX)) {
        const apexT  = apexTickerMap[apexSym];
        const dydxMkt = dydxMap[dydxTkr];
        if (!dydxMkt || dydxMkt.status !== 'ACTIVE') continue;

        const base = apexBase(apexSym);

        // Apex bid/ask/funding — use mark price as fallback
        const apexMark = apexT ? parseFloat(apexT.markPrice || apexT.lastPrice || '0') : 0;
        const apexBid  = apexT ? parseFloat(apexT.bidPrice  || '0') || apexMark * 0.9999 : 0;
        const apexAsk  = apexT ? parseFloat(apexT.askPrice  || '0') || apexMark * 1.0001 : 0;
        const apexMid  = (apexBid + apexAsk) / 2;
        const apexSpread = apexMid > 0 ? (apexAsk - apexBid) / apexMid : 0;

        const apexFunding = apexT ? parseFloat(apexT.nextFundingRate || apexT.fundingRate || '0') : 0;

        // dYdX bid/ask from oracle (orderbook fetch optional for speed)
        const dydxOracle = parseFloat(dydxMkt.oraclePrice || '0');
        const dydxBid    = dydxOracle * 0.9999;
        const dydxAsk    = dydxOracle * 1.0001;
        const dydxFunding = parseFloat(dydxMkt.nextFundingRate || '0');
        const dydxImf    = parseFloat(dydxMkt.initialMarginFraction || '0');
        const dydxMaxLev = dydxImf > 0 ? Math.floor(1 / dydxImf) : 0;

        // Funding arb rates (per funding period, convert to hourly)
        // Both exchanges pay/receive every 8h → hourly = rate / 8
        const apexFundingHourly  = apexFunding / 8;
        const dydxFundingHourly  = dydxFunding / 8;

        const netS_D_L_A = dydxFundingHourly - apexFundingHourly;  // Short dYdX, Long Apex
        const netL_D_S_A = apexFundingHourly - dydxFundingHourly;  // Long dYdX, Short Apex

        let bestRate = 0;
        let bestDir: Row['bestDir'] = 'NONE';
        if (netS_D_L_A >= netL_D_S_A && netS_D_L_A > 0) { bestRate = netS_D_L_A; bestDir = 'S_DYDX_L_APEX'; }
        else if (netL_D_S_A > 0)                          { bestRate = netL_D_S_A; bestDir = 'L_DYDX_S_APEX'; }

        built.push({
          base,
          apexSymbol:      apexSym,
          dydxSymbol:      dydxTkr,
          apexBid,
          apexAsk,
          apexMidSpreadPct: apexSpread,
          apexFunding:     apexFundingHourly,
          apexOracle:      apexMark,
          apexMaxLev:      apexMaxLevMap[apexSym] || 20,
          dydxBid,
          dydxAsk,
          dydxMidSpreadPct: 0.0002,
          dydxFunding:     dydxFundingHourly,
          dydxOracle,
          dydxMaxLev,
          netShortDydxLongApex: netS_D_L_A,
          netLongDydxShortApex: netL_D_S_A,
          bestRate,
          bestDir,
        });
      }

      built.sort((a, b) => b.bestRate - a.bestRate);
      setRows(built);
      setLastUpdate(new Date());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const id = setInterval(fetchData, 30_000);
    return () => clearInterval(id);
  }, [fetchData]);

  // Fetch live orderbooks for first few rows to get real bid/ask
  useEffect(() => {
    if (rows.length === 0) return;
    const topRows = rows.slice(0, 8);
    Promise.all(
      topRows.map(async row => {
        try {
          const r = await fetch(`${DYDX_API}/orderbooks/perpetualMarket/${row.dydxSymbol}`);
          const d: DydxOrderbook = await r.json();
          return { sym: row.dydxSymbol, ob: d };
        } catch { return null; }
      })
    ).then(results => {
      setRows(prev => prev.map(row => {
        const r = results.find(x => x?.sym === row.dydxSymbol);
        if (!r?.ob) return row;
        const dydxBid = r.ob.bids?.[0] ? parseFloat(r.ob.bids[0].price) : row.dydxBid;
        const dydxAsk = r.ob.asks?.[0] ? parseFloat(r.ob.asks[0].price) : row.dydxAsk;
        return { ...row, dydxBid, dydxAsk };
      }));
    });
  }, [rows.length]); // eslint-disable-line

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-xs font-semibold uppercase tracking-widest rounded-t-lg transition-colors ${
      active ? 'bg-[#0a2560] text-[#4A9FE8] border-b-2 border-[#4A9FE8]' : 'text-blue-500 hover:text-blue-300'
    }`;

  const openRows   = arbHistory.filter(r => r.status === 'open');
  const closedRows = arbHistory.filter(r => r.status === 'closed');

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} p-6`}>

      {/* Modals */}
      {modalRow && (
        <OrderModal row={modalRow} dark={dark} onClose={() => setModalRow(null)} sendWs={sendWs} onRecordOpen={handleRecordOpen} />
      )}
      {closeCard && (
        <CloseModal card={closeCard} dark={dark} onClose={() => setCloseCard(null)} sendWs={sendWs} onRecordClose={handleRecordClose} />
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-lg font-black tracking-widest text-[#4A9FE8] uppercase">dYdX ↔ Apex Pro</h1>
          <p className="text-xs text-blue-500 mt-0.5">Cross-DEX Funding Arbitrage Scanner</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-[11px] text-blue-600">{lastUpdate.toLocaleTimeString('tr-TR')} güncellendi</span>
          )}
          <button
            onClick={() => { setLoading(true); fetchData(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0a2560] hover:bg-[#0d3080] text-blue-300 text-xs transition-colors"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#0a2560] mb-5">
        <button className={tabClass(tab === 'dashboard')} onClick={() => setTab('dashboard')}>Dashboard</button>
        <button className={tabClass(tab === 'gecmis')}    onClick={() => setTab('gecmis')}>
          Geçmiş {arbHistory.length > 0 && <span className="ml-1 text-[9px] bg-[#4A9FE8]/20 text-[#4A9FE8] px-1.5 py-0.5 rounded-full">{arbHistory.length}</span>}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-rose-900/20 border border-rose-500/30 text-rose-400 text-xs">
          ⚠ {error}
        </div>
      )}

      {/* ── DASHBOARD ──────────────────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div className="space-y-5">
          {/* Fee cards */}
          <div className="grid grid-cols-2 gap-3 max-w-sm">
            <FeeCard label="dYdX Fees" maker={DYDX_MAKER} taker={DYDX_TAKER} dark={dark} />
            <FeeCard label="Apex Pro Fees" maker={APEX_MAKER} taker={APEX_TAKER} dark={dark} color="orange" />
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-blue-500 text-sm py-8 justify-center">
              <RefreshCw size={14} className="animate-spin" /> Veriler yükleniyor...
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#0a2560]">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#0a2560]">
                    <th className="text-left px-3 py-2.5 text-[#4A9FE8] font-semibold uppercase tracking-wider">Asset</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Bid</th>
                    <th className="text-right px-2 py-2.5 text-blue-400 font-semibold">dYdX Ask</th>
                    <th className="text-right px-3 py-2.5 text-blue-400 font-semibold">Funding/h</th>
                    <th className="px-1" />
                    <th className="text-right px-2 py-2.5 text-orange-400 font-semibold">Apex Bid</th>
                    <th className="text-right px-2 py-2.5 text-orange-400 font-semibold">Apex Ask</th>
                    <th className="text-right px-3 py-2.5 text-orange-400 font-semibold">Funding/h</th>
                    <th className="px-1" />
                    <th className="text-right px-3 py-2.5 text-[#4A9FE8] font-semibold">Best Arb / h</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.base}
                      className={`border-b border-[#0a2560]/50 hover:bg-[#0a2560]/30 transition-colors ${
                        row.bestRate > 0.0001 ? 'bg-emerald-950/10' : ''
                      }`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-bold text-white">{row.base}</span>
                          <span className="text-[10px] text-blue-600">{row.dydxSymbol}</span>
                        </div>
                      </td>
                      {/* dYdX */}
                      <td className="text-right px-2 py-2.5 font-mono text-emerald-400">{fmt(row.dydxBid)}</td>
                      <td className="text-right px-2 py-2.5 font-mono text-rose-400">{fmt(row.dydxAsk)}</td>
                      <td className="text-right px-3 py-2.5"><FundingBadge rate={row.dydxFunding} /></td>
                      <td className="px-1"><div className="w-px h-6 bg-[#0a2560] mx-auto" /></td>
                      {/* Apex */}
                      <td className="text-right px-2 py-2.5 font-mono text-emerald-400">
                        {row.apexBid > 0 ? fmt(row.apexBid) : <span className="text-blue-600">—</span>}
                      </td>
                      <td className="text-right px-2 py-2.5 font-mono text-rose-400">
                        {row.apexAsk > 0 ? fmt(row.apexAsk) : <span className="text-blue-600">—</span>}
                      </td>
                      <td className="text-right px-3 py-2.5"><FundingBadge rate={row.apexFunding} /></td>
                      <td className="px-1"><div className="w-px h-6 bg-[#0a2560] mx-auto" /></td>
                      {/* Arb */}
                      <td className="text-right px-3 py-2.5">
                        <ArbButton row={row} onOpen={r => setModalRow(r)} />
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-blue-600">Veri bulunamadı</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY ──────────────────────────────────────────────────────── */}
      {tab === 'gecmis' && (
        <div className="space-y-5">

          {/* Open positions */}
          {openRows.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-[#4A9FE8] mb-3 uppercase tracking-wider">Açık Pozisyonlar ({openRows.length})</h2>
              <div className="space-y-2">
                {openRows.map(r => {
                  const matchRow = rows.find(row => row.dydxSymbol === r.dydxSymbol) || null;
                  return (
                    <div key={r.id} className="rounded-xl border border-emerald-700/40 bg-emerald-950/20 p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-white">{r.base}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${r.dydxDir === 'long' ? 'bg-emerald-900/40 text-emerald-400 border-emerald-700/60' : 'bg-rose-900/40 text-rose-400 border-rose-700/60'}`}>
                            dYdX {r.dydxDir.toUpperCase()}
                          </span>
                          <span className={`text-[11px] px-2 py-0.5 rounded-full border font-bold ${r.apexDir === 'long' ? 'bg-emerald-900/40 text-emerald-400 border-emerald-700/60' : 'bg-rose-900/40 text-rose-400 border-rose-700/60'}`}>
                            Apex {r.apexDir.toUpperCase()}
                          </span>
                          <span className="text-[10px] text-blue-500 font-mono">{r.qty.toFixed(6)} token</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-blue-600">
                            {new Date(r.openTime).toLocaleString('tr-TR')}
                          </span>
                          <button
                            onClick={() => setCloseCard({
                              base:       r.base,
                              dydxTkr:    r.dydxSymbol,
                              apexSymbol: r.apexSymbol,
                              dydxSide:   r.dydxDir.toUpperCase() as 'LONG' | 'SHORT',
                              apexSide:   r.apexDir,
                              dydxSize:   r.qty,
                              apexSize:   r.qty,
                              matchRow,
                            })}
                            className="px-3 py-1.5 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-300 text-[11px] font-semibold hover:bg-rose-500/30 transition-colors"
                          >
                            Kapat
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                        <div className="flex justify-between">
                          <span className="text-blue-500">dYdX giriş</span>
                          <span className="font-mono text-white">{fmt(r.dydxOpenPrice)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-blue-500">Apex giriş</span>
                          <span className="font-mono text-orange-300">{fmt(r.apexOpenPrice)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Closed positions */}
          {closedRows.length > 0 && (
            <div>
              <h2 className="text-sm font-bold text-blue-400 mb-3 uppercase tracking-wider">Kapatılan Pozisyonlar ({closedRows.length})</h2>
              <div className="overflow-x-auto rounded-xl border border-[#0a2560]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#0a2560]">
                      <th className="text-left px-3 py-2 text-[#4A9FE8]">Asset</th>
                      <th className="text-left px-2 py-2 text-blue-400">Yön</th>
                      <th className="text-right px-2 py-2 text-blue-400">Miktar</th>
                      <th className="text-right px-2 py-2 text-blue-400">dYdX Giriş/Çıkış</th>
                      <th className="text-right px-2 py-2 text-orange-400">Apex Giriş/Çıkış</th>
                      <th className="text-right px-2 py-2 text-blue-400">Süre</th>
                      <th className="text-right px-2 py-2 text-blue-400">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...closedRows].reverse().map(r => {
                      const durationMs = r.closeTime ? r.closeTime - r.openTime : 0;
                      const durationH  = durationMs / 3600000;
                      return (
                        <tr key={r.id} className="border-b border-[#0a2560]/50 hover:bg-[#0a2560]/20">
                          <td className="px-3 py-2 font-bold text-white">{r.base}</td>
                          <td className="px-2 py-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${r.dydxDir === 'long' ? 'text-emerald-400 border-emerald-700/50' : 'text-rose-400 border-rose-700/50'}`}>
                              {r.dydxDir.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-right px-2 py-2 font-mono text-white">{r.qty.toFixed(6)}</td>
                          <td className="text-right px-2 py-2 font-mono text-blue-300">
                            {fmt(r.dydxOpenPrice)} → {r.dydxClosePrice ? fmt(r.dydxClosePrice) : '—'}
                          </td>
                          <td className="text-right px-2 py-2 font-mono text-orange-300">
                            {fmt(r.apexOpenPrice)} → {r.apexClosePrice ? fmt(r.apexClosePrice) : '—'}
                          </td>
                          <td className="text-right px-2 py-2 text-blue-400">
                            {durationH > 0 ? `${durationH.toFixed(1)}h` : '—'}
                          </td>
                          <td className="text-right px-2 py-2 text-blue-600">
                            {new Date(r.openTime).toLocaleDateString('tr-TR')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {arbHistory.length === 0 && (
            <div className="text-center py-12 text-blue-600">
              <p className="text-sm">Henüz işlem kaydı yok.</p>
              <p className="text-xs mt-1">Dashboard'dan arb emri göndererek buraya kayıt ekleyebilirsin.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
